import { getLocalStorageJSONPreference } from 'javascripts/utils';
import { type RecordId } from 'javascripts/shared-types';
import { type ReferenceAhaObject } from '@aha-app/aha-editor-v2';
import {
  defaultFields,
  fieldsForItem,
  RECORD_CLASSES,
} from './components/pillFields';

export const prefKeyForItemClass = (itemClass: ReferenceAhaObject['class']) => {
  // These are used interchangeably, so store the preference under the same key
  // (I chose `Ideas::Idea` as the default since it is the most widely used one prior
  // to this change)
  if (itemClass === 'Idea') itemClass = 'Ideas::Idea';

  return `ReferenceAhaPill.visibleFields.${itemClass}`;
};

export const getVisibleFieldsForItemClass = (
  itemClass: ReferenceAhaObject['class']
): string[] => {
  const allKeys = fieldsForItem(itemClass).map(field => field.key);
  let visibleFields: string[];
  const lastUsedKeys = getLocalStorageJSONPreference(
    prefKeyForItemClass(itemClass)
  );

  if (Array.isArray(lastUsedKeys)) {
    visibleFields = allKeys.filter(key => lastUsedKeys.includes(key));

    if (visibleFields.length === 0) {
      return allKeys;
    } else {
      return visibleFields;
    }
  } else {
    return defaultFields(itemClass) || allKeys;
  }
};

const AHA_REFERENCE_REGEX = new RegExp(
  `(key_results|competitors|discovery_interviews|discovery_studies|epics|features|master_features|master_releases|releases|initiatives|iterations|strategic_imperatives|ideas\\/ideas|requirements|pages|personas|tasks|ideas\\/idea_themes|polls|ideas\\/idea_organizations)(/)(\\d+|\\w+-((N|R|I|S|SP|G|G-\\d+-KR|E|P|C|IN|ST|POLL|T|O|\\d+)-)?\\d+)$`,
  'i'
);
const AHA_REFERENCE_CLASSES = {
  competitors: 'Competitor',
  discovery_interviews: 'Discovery::Interview',
  discovery_studies: 'Discovery::Study',
  epics: 'Epic',
  master_features: 'MasterFeature',
  features: 'Feature',
  requirements: 'Requirement',
  releases: 'Release',
  master_releases: 'MasterRelease',
  pages: 'Page',
  personas: 'Persona',
  initiatives: 'Initiative',
  iterations: 'Iteration',
  'ideas/ideas': 'Ideas::Idea',
  strategic_imperatives: 'StrategicImperative',
  tasks: 'Task',
  key_results: 'KeyResult',
  polls: 'Polls::Poll',
  'ideas/idea_themes': 'Ideas::IdeaTheme',
  'ideas/idea_organizations': 'Ideas::IdeaOrganization',
};

const ID_REGEX = /^\d+$/;

type ReferenceLink = {
  url: string;
  linkUrl: string;
  class: ReferenceAhaObject['class'];
  id?: RecordId;
  reference_num?: string;
  name?: string;
  visibleFields: string[];
};

type AsyncReferenceLink = {
  reference: ReferenceLink;
  fetchData: () => Promise<{ id: RecordId; name: string | null }>;
};

export const detectReferenceLink = (text: unknown) => {
  if (typeof text !== 'string') {
    return;
  }

  // Check if URL can be parsed
  // TODO: URL.canParse() is pretty new, can't use it quite yet
  let url: URL;
  try {
    url = new URL(text);
  } catch (ex) {
    return;
  }

  if (url.origin !== window.location.origin) {
    return;
  }

  const match = text.match(AHA_REFERENCE_REGEX);
  if (!match || !(match[1] in AHA_REFERENCE_CLASSES)) {
    return;
  }

  const itemClass: ReferenceAhaObject['class'] =
    AHA_REFERENCE_CLASSES[match[1]];
  const visibleFields = getVisibleFieldsForItemClass(itemClass);

  const modelClass = RECORD_CLASSES[itemClass];
  if (!modelClass) {
    return;
  }

  const reference: ReferenceLink = {
    url: `${text}?reference-class=${itemClass}`,
    linkUrl: text,
    class: itemClass,
    reference_num: undefined,
    name: undefined,
    visibleFields,
  };

  if (!ID_REGEX.test(match[3])) {
    if (modelClass.hasField('referenceNum')) {
      reference.reference_num = match[3];
    }

    // Temporary record name from ref num
    if (modelClass.hasField('name')) {
      reference.name = match[3];
    }
  } else {
    reference.id = match[3] as RecordId;

    return new Promise<AsyncReferenceLink>((resolve, reject) => {
      resolve({
        reference,
        fetchData: async () => {
          const record = await modelClass.select('id', 'name').find(match[3]);

          return {
            id: record.id,
            name: record.name,
          };
        },
      });
    });
  }

  return reference;
};
